// src/hooks/attainment-hooks.ts
//
// CENTRO goal-attainment drill for the Leader "Enablement" tab, addressed by email.
//
// `GET snowflake/goal/attainment/drill/by-email/?email=` returns the same SHALLOW envelope shape as the
// education drill: one org node's `own` attainment (quota / bookings / attainment_pct), its whole-subtree
// `rollup`, and each DIRECT report as a stub with its own + rollup figures. The frontend drills by
// calling again for a report's email, keeping each payload proportional to one node's span-of-control.
// `attainment_pct` is a fraction (0–1) or null; quota/bookings are dollars or null. Org-scoped and
// live-only server-side (400s in local/demo mode), so callers degrade gracefully on error.

import { useQueries, useQuery } from '@tanstack/react-query'; //-tech-debt-react-query;
import { apiGet } from '../support/aiApi';
import { DEFAULT_QUERY_OPTIONS } from '../support/constants';

/**
 * One plan-element bucket within a goaling period — e.g. `SEC-TTL` / `ACV` / `NPR`. quota/bookings are
 * dollars; attainment_pct is 0–1 (or null). `primary` flags the headline element for the period.
 */
export interface AttainmentPeriodElement {
  category: string | null;
  type: string | null;
  primary: boolean;
  quota: number | null;
  bookings: number | null;
  attainment_pct: number | null;
}

/**
 * One goaling period for a person: a denoting `label` (e.g. `"H2 FY26"`), its 2–3 plan-element buckets,
 * and a per-period `total` summed across that period's elements ONLY (never across periods, so it is not
 * inflated the way an all-period sum would be). Periods arrive newest-first.
 */
export interface AttainmentPeriod {
  sheet_id: number | null;
  interval: string | null;
  label: string | null;
  start: string | null;
  end: string | null;
  elements: AttainmentPeriodElement[];
  total: { quota: number | null; bookings: number | null; attainment_pct: number | null };
}

/** One node's attainment figures. quota/bookings are dollars; attainment_pct is 0–1 (or null). */
export interface AttainmentStats {
  /** Display name for the node (present on `own`; absent on `rollup`). */
  name?: string | null;
  quota: number | null;
  bookings: number | null;
  attainment_pct: number | null;
  /** Per-goaling-period plan-element buckets (present on a node's `own`, absent on `rollup`). */
  periods?: AttainmentPeriod[];
  /** Plan elements summed over the node's whole subtree for the applied interval (present on `rollup`
   *  once the server supports it). Same shape as a period's `elements`. */
  elements?: AttainmentPeriodElement[];
}

/** A direct report of a drill node — own + rollup attainment (drill again by email to descend). */
export interface AttainmentDrillReport {
  email: string;
  cecid: string | null;
  is_manager: boolean;
  own: AttainmentStats;
  rollup: AttainmentStats;
}

/** One org node addressed by email: own attainment + subtree rollup + direct-report stubs. */
export interface AttainmentDrillNode {
  email: string;
  cecid: string | null;
  is_manager: boolean;
  own: AttainmentStats;
  rollup: AttainmentStats;
  reports: AttainmentDrillReport[];
}

/** A selectable goaling interval's identity (no figures) — the `available_periods` entries. */
export type AttainmentPeriodMeta = Pick<
  AttainmentPeriod,
  'sheet_id' | 'interval' | 'label' | 'start' | 'end'
>;

export interface AttainmentDrillResponse {
  success: boolean;
  /** The `?period=` the server applied (label as requested); absent/null when it defaulted or the
   *  server predates interval selection. */
  period?: string | null;
  /** Every goaling interval present in the node's subtree, newest first. */
  available_periods?: AttainmentPeriodMeta[];
  source?: string;
  query_name?: string;
  email?: string;
  is_manager?: boolean;
  scope_size?: number;
  node: AttainmentDrillNode | null;
}

/**
 * The attainment drill node for a single `email` (the person the dashboard is currently focused on).
 * `enabled` only when an email is supplied; errors (local-mode 400 / out-of-scope 403) surface as
 * `isError` with no data so the Enablement pages simply omit the attainment tiles.
 */
export function useAttainmentByEmail(
  email?: string,
  opts: { queryOptions?: object } = {},
) {
  const target = String(email || '').trim().toLowerCase();
  return useQuery<AttainmentDrillResponse>({
    queryKey: ['attainment-drill-by-email', target],
    queryFn: () =>
      apiGet<AttainmentDrillResponse>('snowflake/goal/attainment/drill/by-email/', {
        email: target,
      }),
    ...{ ...DEFAULT_QUERY_OPTIONS, retry: false, enabled: !!target, ...(opts.queryOptions || {}) },
  });
}

const ATTAINMENT_CHAIN_PAGE_SIZE = 1000;

/**
 * One drill node per email in `emails` (the Leader > Attainment navigation path, root first), fetched in
 * parallel with `page=1&size=1000` so a wide span-of-control isn't truncated by the server default. The
 * Attainment tab derives its whole reporting structure from these nodes rather than the User Access org
 * chart, so `nodes[i]` is `null` until that level has loaded (or if it errored / is out of scope).
 *
 * `period` (a goaling-period label such as `H1 FY27`) is sent as `?period=` to view an interval other
 * than the server default (each person's newest). Omit it for the default call. `appliedPeriods[i]` is the
 * period each response echoes back, so callers can tell when a server ignored the parameter.
 */
export function useAttainmentChain(emails: string[], enabled = true, period?: string) {
  const targets = emails.map((e) => String(e || '').trim().toLowerCase());
  const results = useQueries({
    queries: targets.map((target) => ({
      queryKey: ['attainment-drill-chain', target, period ?? null],
      queryFn: () =>
        apiGet<AttainmentDrillResponse>('snowflake/goal/attainment/drill/by-email/', {
          email: target,
          page: 1,
          size: ATTAINMENT_CHAIN_PAGE_SIZE,
          ...(period ? { period } : {}),
        }),
      ...DEFAULT_QUERY_OPTIONS,
      retry: false,
      enabled: enabled && !!target,
    })),
  });
  const data = results.map((r) => r.data as AttainmentDrillResponse | undefined);
  return {
    nodes: data.map((d) => d?.node ?? null),
    appliedPeriods: data.map((d) => d?.period ?? null),
    availablePeriods: (data.find((d) => d?.available_periods?.length)?.available_periods ??
      []) as AttainmentPeriodMeta[],
    isLoading: results.some((r) => r.isLoading),
    error: (results.find((r) => r.isError)?.error as Error | undefined) ?? null,
  };
}
